import { z } from "zod";

import { type Database } from "@fitness-app/supabase";

import { MOBILE_UPDATE_TYPES } from "./AppRelease";
import { helpBlockSchema } from "./HelpContent";

// Centrum pomocy dla klientek (TRE-2126). Jedno źródło prawdy dla panelu,
// client-weba, agenta ze Slacka i skryptów lokalnych — każde z nich pisze do
// `help_article`, więc reguły nie mogą żyć osobno w każdej ścieżce.
export type HelpArticleEntity = Database["public"]["Tables"]["help_article"]["Row"];
export type HelpMediaEntity = Database["public"]["Tables"]["help_media"]["Row"];

export const HELP_ARTICLE_KINDS = ["guide", "update"] as const;
export type HelpArticleKind = (typeof HELP_ARTICLE_KINDS)[number];

export const HELP_ARTICLE_STATUSES = ["draft", "verified", "published"] as const;
export type HelpArticleStatus = (typeof HELP_ARTICLE_STATUSES)[number];

export const HELP_ARTICLE_STATUS_LABELS: Record<HelpArticleStatus, string> = {
  draft: "Szkic",
  verified: "Sprawdzony",
  published: "Opublikowany",
};

// Zbiór jest lustrem constraintu `help_article_category_check` w bazie ORAZ mapy
// `categories` w `apps/client-web/src/features/help/model.ts` (etykiety filtra
// dla klientki). Dopisanie kategorii w jednym miejscu przepuści zapis, ale
// materiał wypadnie z filtra albo wywali się na walidacji.
export const HELP_CATEGORIES = {
  start: "Pierwsze kroki",
  dieta: "Dieta i posiłki",
  trening: "Treningi",
  postepy: "Postępy",
  konto: "Konto i kontakt",
} as const;
export type HelpCategory = keyof typeof HELP_CATEGORIES;

// Instrukcje mają jeden wybór „Aplikacja” dla iOS i Androida — nie powielamy
// tej samej instrukcji na każdy system. Rozróżnić je może wyłącznie wpis
// „Co nowego”, bo wydania idą osobno.
export const HELP_CLIENT_PLATFORMS = { mobile: "Aplikacja", web: "Przeglądarka" } as const;
export const HELP_PLATFORMS = { ...HELP_CLIENT_PLATFORMS, ios: "iOS", android: "Android" } as const;
export type HelpPlatform = keyof typeof HELP_PLATFORMS;

export const HELP_CHANGE_TYPES = { new: "Nowość", improvement: "Ulepszenie", fix: "Poprawka" } as const;
export type HelpChangeType = keyof typeof HELP_CHANGE_TYPES;

// `wersja natywna (build) - wersja JS` — tak numery wydań wyglądają w
// ogłoszeniach releasowych i tak trafiają do wpisu.
const mobileVersion = z
  .string()
  .trim()
  .regex(/^\d+\.\d+\.\d+ \(\d+\) - \d+\.\d+\.\d+$/);
const webVersion = z
  .string()
  .trim()
  .regex(/^\d+\.\d+\.\d+$/);

export const helpReleaseSchema = z.discriminatedUnion("platform", [
  z.object({ platform: z.literal("ios"), version: mobileVersion, updateType: z.enum(MOBILE_UPDATE_TYPES).nullable() }),
  z.object({
    platform: z.literal("android"),
    version: mobileVersion,
    updateType: z.enum(MOBILE_UPDATE_TYPES).nullable(),
  }),
  z.object({ platform: z.literal("web"), version: webVersion, updateType: z.literal("web") }),
]);

export type HelpRelease = z.infer<typeof helpReleaseSchema>;

export const helpSlugSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
  // `/pomoc/co-nowego` to lista wpisów, nie materiał — slug o tej nazwie
  // przykryłby ją własnym artykułem.
  .refine((slug) => slug !== "co-nowego");

const text = z.string().trim().min(1);

export const helpArticleMetadataSchema = z.object({
  kind: z.enum(HELP_ARTICLE_KINDS),
  slug: helpSlugSchema,
  title: text,
  summary: text,
  availability: z.string().trim(),
  category: z.enum(Object.keys(HELP_CATEGORIES) as [HelpCategory, ...HelpCategory[]]),
  platforms: z.array(z.enum(Object.keys(HELP_PLATFORMS) as [HelpPlatform, ...HelpPlatform[]])).min(1),
  keywords: z.array(text),
  related: z.array(text),
  changeType: z.enum(Object.keys(HELP_CHANGE_TYPES) as [HelpChangeType, ...HelpChangeType[]]).nullable(),
  releasedAt: z.iso.date().nullable(),
  releases: z.array(helpReleaseSchema).nullable(),
});

export type HelpArticleMetadata = z.infer<typeof helpArticleMetadataSchema>;

export interface HelpPublishCandidate extends Partial<HelpArticleMetadata> {
  publishedAt?: string | null;
  content?: unknown;
}

/**
 * Powody, dla których materiału nie wolno wypuścić klientkom — po polsku, bo
 * lądują wprost przy przycisku „Publikuj”. Baza trzyma te same reguły jako
 * checki (`20260910120000_help_center_content`); tutaj chodzi o to, żeby
 * redaktorka zobaczyła, czego brakuje, zanim zapis odbije się o RLS.
 */
export const helpPublishBlockers = (article: HelpPublishCandidate): string[] => {
  const blockers: string[] = [];

  if (article.kind === "update" && !article.changeType) {
    blockers.push("Wpis „Co nowego” wymaga rodzaju zmiany.");
  }

  if (article.kind === "update" && !article.releasedAt) {
    blockers.push("Wpis „Co nowego” wymaga daty wydania funkcji.");
  }

  if (Array.isArray(article.content)) {
    if (article.content.length === 0) {
      blockers.push("Materiał nie ma treści.");
    }

    // Blok bez kompletu danych (krok bez tytułu, obraz bez opisu dla czytnika,
    // film bez opisu tekstowego) nie przechodzi schemy, więc renderer klientek
    // po prostu by go pominął. Lepiej powiedzieć o tym przed publikacją niż
    // wypuścić materiał z dziurą.
    const broken = article.content.filter((block) => !helpBlockSchema.safeParse(block).success);
    if (broken.length) {
      blockers.push(`Uzupełnij niedokończone bloki treści (${broken.length}).`);
    }
  }

  const articlePlatforms = new Set(article.platforms ?? []);
  for (const release of article.releases ?? []) {
    if (release.updateType === null) {
      // Dwa wydania czerwcowe z TRE-2107 stoją dokładnie w tym miejscu: numery
      // są znane, sposób dostarczenia nie. Klientka dostałaby wpis bez
      // odpowiedzi „skąd wezmę tę wersję”.
      blockers.push(`Potwierdź sposób aktualizacji wydania ${release.platform}.`);
    }

    const matchesPlatform =
      articlePlatforms.has(release.platform) || (release.platform !== "web" && articlePlatforms.has("mobile"));
    if (!matchesPlatform) {
      blockers.push(`Wydanie ${release.platform} nie pasuje do platform wpisu.`);
    }
  }

  if (article.kind === "guide" && (articlePlatforms.has("ios") || articlePlatforms.has("android"))) {
    blockers.push("Instrukcje aplikacji są wspólne: wybierz „Aplikacja” zamiast iOS/Androida.");
  }

  return blockers;
};

export const isHelpArticlePublic = (
  article: Pick<HelpArticleEntity, "status" | "publishedAt">,
  now = new Date(),
): boolean => article.status === "published" && !!article.publishedAt && new Date(article.publishedAt) <= now;

// Ogłoszenie wpisu „Co nowego” ------------------------------------------------

/**
 * Ogłoszenie to popup u klientki, nie wysyłka: `announcedAt` jest właściwością
 * wpisu, a nie kampanią ani kolejką (patrz migracja
 * `20260912120000_help_article_announcement`). Popup wygasa sam po
 * `announceUntil` — bez tego okna nowość sprzed dwóch miesięcy witałaby nową
 * klientkę przy pierwszym wejściu.
 */
export const HELP_ANNOUNCE_DEFAULT_DAYS = 14;
export const HELP_ANNOUNCE_DEFAULT_CTA = "Zobacz, co nowego";

export type HelpArticleAnnouncement = Pick<
  HelpArticleEntity,
  "announcedAt" | "announceUntil" | "announceCtaText" | "announcePushSentAt"
>;

/**
 * Do kogo ma trafić ogłoszenie. Nie każda nowość jest dla wszystkich: funkcja
 * bywa wpięta w jeden produkt albo w pilotaż oznaczony tagiem. Puste listy =
 * bez zawężenia; zawężenia łączą się iloczynem, a wewnątrz listy wystarczy
 * jedno trafienie — ta sama semantyka co w kampaniach, żeby redakcja nie
 * musiała uczyć się drugiej.
 *
 * Kryteria żyją w osobnej tabeli (`help_announcement_target`), bo sam wpis
 * czyta każda klientka, a nazwy tagów i lista produktów to wiedza wewnętrzna.
 */
export interface HelpAnnounceTargetOptions {
  productIds: string[];
  productTypes: string[];
  tags: string[];
  tagsCondition: "hasTag" | "notHasTag" | null;
}

/**
 * Do kogo idzie push ogłoszenia (TRE-2259). `release` - tylko do klientek, które
 * mają już wydanie z tą zmianą. `all` - pozostałe dostają push z prośbą
 * o aktualizację aplikacji: przy wydaniu ze sklepu ogłoszonym od razu to
 * zwykle większość bazy, a właśnie je chcemy namówić do aktualizacji.
 */
export type HelpPushAudience = "release" | "all";

export interface HelpAnnounceOptions {
  /** Popup u klientek. Bez niego wpis publikuje się cicho — tylko na liście „Co nowego”. */
  announce: boolean;
  /** Push tylko razem z popupem: sam push prowadziłby na ekran, gdzie nic nie czeka. */
  push: boolean;
  pushAudience: HelpPushAudience;
  ctaText: string;
  /** Dni widoczności popupu, liczone od ogłoszenia. */
  visibleForDays: number;
  target: HelpAnnounceTargetOptions;
}

export const emptyHelpAnnounceTarget = (): HelpAnnounceTargetOptions => ({
  productIds: [],
  productTypes: [],
  tags: [],
  tagsCondition: null,
});

export const defaultHelpAnnounceOptions = (kind: HelpArticleKind): HelpAnnounceOptions => ({
  // Instrukcji nie ogłaszamy — nowością jest wpis „Co nowego”, a baza i tak
  // odbija ogłoszenie instrukcji checkiem `help_article_announce_only_updates`.
  announce: kind === "update",
  push: kind === "update",
  pushAudience: "all",
  ctaText: HELP_ANNOUNCE_DEFAULT_CTA,
  visibleForDays: HELP_ANNOUNCE_DEFAULT_DAYS,
  target: emptyHelpAnnounceTarget(),
});

export const isHelpAnnouncementLive = (
  // Tylko okno — żeby tę samą funkcję mógł wołać i serwis (CTA ani pusha nie
  // czyta), i klient dostający wpis z `help_announcements_for_me`, gdzie okno
  // sprawdziła już baza i `announceUntil` w ogóle nie przychodzi.
  article: { announcedAt: string | null; announceUntil?: string | null },
  now = new Date(),
): boolean =>
  !!article.announcedAt &&
  new Date(article.announcedAt) <= now &&
  (!article.announceUntil || new Date(article.announceUntil) > now);

/**
 * Powody, dla których wpisu nie wolno ogłosić — lustro bramek z triggera
 * `help_article_guard_publication`, pokazane redaktorce zanim baza odbije zapis.
 */
export const helpAnnounceBlockers = (
  article: Pick<HelpArticleEntity, "kind" | "status"> & Partial<HelpArticleAnnouncement>,
): string[] => {
  const blockers: string[] = [];

  if (article.kind !== "update") {
    blockers.push("Ogłaszać można wyłącznie wpisy „Co nowego”.");
  }

  if (article.status !== "published") {
    // Popup prowadzi na stronę wpisu — ogłoszenie szkicu kończy się pustą stroną.
    blockers.push("Najpierw opublikuj materiał.");
  }

  return blockers;
};

/**
 * Lustro `public.help_normalize_search` z bazy i `normalizeSearch` z
 * client-weba: klientka wpisuje „posilek” i ma znaleźć „posiłek”.
 */
export const normalizeHelpSearch = (value: string): string =>
  value
    .toLocaleLowerCase("pl")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/ł/g, "l");
